import {
  BaseSyntheticEvent,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import useOrderDetails from './useOrderDetails';
import { getMonthsOptions } from '@app/utils';
import { NullableNumber } from '@app/common/types';
import { useForm } from 'react-hook-form';
import { FormData } from './types';
import useLoadOrderDetailValues from './useOrderDetailsValues';
import Loader from '@app/common/loadData/Loader';
import EditPersonal from './editPersonal';
import useSaveOrder from './useSaveOrder';
import getRegisteredFieldsForEditPersonal from './getRegisteredFieldsForEditPersonal';
import { ValueType } from 'react-select';
import { SelectOption } from '@app/common/types';

type EditOrderFormI = {
  handleClose: () => void;
  id: NullableNumber;
  setShouldReload: (value: boolean) => void;
  isOrderStatusDone?: boolean;
};

const EditOrderFormPersonal = ({
  id,
  handleClose,
  setShouldReload,
  isOrderStatusDone,
}: EditOrderFormI): ReactElement => {
  const { orderDetails, loading } = useOrderDetails(id);

  const {
    register,
    reset,
    handleSubmit,
    formState,
    getValues,
    clearErrors,
    setValue,
  } = useForm<FormData>();
  const currentValues = getValues();

  const personalRegisteredFields = getRegisteredFieldsForEditPersonal(
    register,
    clearErrors,
    isOrderStatusDone
  );
  const { loading: saveLoading, saveOrder } = useSaveOrder(orderDetails);
  const { errors, isSubmitSuccessful } = formState;
  useLoadOrderDetailValues(reset, orderDetails);

  const months = getMonthsOptions();

  const docTypes = [
    {
      label: 'Passport',
      value: 'passport',
    },
  ];

  const handleSavePersonalInformation = (event: BaseSyntheticEvent) => {
    event.preventDefault();
    handleSubmit(saveOrder)(event);
  };

  const onClose = useCallback(
    (event) => {
      event.preventDefault();
      handleClose();
    },
    [handleClose]
  );

  useEffect(() => {
    if (isSubmitSuccessful && handleClose) {
      setShouldReload(true);
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  const [monthSelectLabel, setMonthSelectLabel] = useState(months[0].label);
  const [dockTypeSelectLabel, setDockTypeSelectLabel] = useState(
    docTypes[0].label
  );

  const handleChangeMonth = (event: ValueType<SelectOption, false>): void => {
    if (event) setValue('birthdateMonth', event.label as string);
    setMonthSelectLabel(getValues('birthdateMonth'));
  };

  const handleChangeDocType = (event: ValueType<SelectOption, false>): void => {
    if (event) setValue('documentType', event.label as string);
    setDockTypeSelectLabel(getValues('documentType'));
  };

  useEffect(() => {
    setMonthSelectLabel(getValues('birthdateMonth'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValues]);

  const currentValueMonth = months.filter(
    (elem) => elem.label === monthSelectLabel
  )[0];

  const currentValueDocType = docTypes.filter(
    (elem) => elem.label === dockTypeSelectLabel
  )[0];

  return loading || saveLoading ? (
    <Loader />
  ) : (
    <form>
      <EditPersonal
        personalRegisteredFields={personalRegisteredFields}
        data={orderDetails}
        handleClose={onClose}
        handleSavePersonalInformation={handleSavePersonalInformation}
        errors={errors}
        setValue={setValue}
        months={months}
        getValues={getValues}
        handleChangeMonth={handleChangeMonth}
        currentValueMonth={currentValueMonth}
        docTypes={docTypes}
        handleChangeDocType={handleChangeDocType}
        currentValueDocType={currentValueDocType}
        isOrderStatusDone={isOrderStatusDone}
      />
    </form>
  );
};

export default EditOrderFormPersonal;
